import { Anchor, Stack, Text } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { handlingUnitsStepMessages } from './handling-units-step-messages';
import { WizardHandlingUnitCard } from './WizardHandlingUnitCard';
import { unitMessages } from '../../../../utils/unit-messages';
import { convertWeight } from '../../../../utils/weight-utils';
import { createEmptyHandlingUnit } from '../../create-empty-handling-unit';
import { sliceFieldErrors } from '../../wizard-step-validation';

import type { DimensionUnit } from '../../../../types/dimensions';
import type { LtlHandlingUnit } from '../../../../types/ltl';
import type { WeightUnit } from '../../../../types/weight';
import type { FreightDensityAction } from '../../../freight-density/RecommendedFreightClassModal';
import type { FreightBookingFormState } from '../../freight-booking-wizard-types';
import type { MessageDescriptor } from 'react-intl';

// Short abbreviation message per weight unit, for the summary's total label.
const WEIGHT_UNIT_ABBREVIATION: Record<WeightUnit, MessageDescriptor> = {
  Pounds: unitMessages.pounds,
  Ounces: unitMessages.ounces,
  Kilograms: unitMessages.kilograms,
  Grams: unitMessages.grams,
};

export type HandlingUnitsStepProps = {
  formState: FreightBookingFormState;
  onFormChange: (next: FreightBookingFormState) => void;
  displayWeightUnit: WeightUnit;
  displayDimensionUnit: DimensionUnit;
  isHazmatEnabled?: boolean;
  onDensityAction?: (action: FreightDensityAction) => void;
  onCommodityPresetSelect?: (presetId: number) => void;
  /** Formatted inline error messages keyed by field path (e.g. `'handlingUnits.0.length'`). */
  fieldErrors?: Record<string, string>;
  zIndex?: number;
};

export const HandlingUnitsStep = ({
  formState,
  onFormChange,
  displayWeightUnit,
  displayDimensionUnit,
  isHazmatEnabled,
  onDensityAction,
  onCommodityPresetSelect,
  fieldErrors,
  zIndex,
}: HandlingUnitsStepProps) => {
  const handleUnitChange = (index: number, updated: LtlHandlingUnit) => {
    const handlingUnits = [...formState.handlingUnits];
    handlingUnits[index] = updated;
    onFormChange({ ...formState, handlingUnits });
  };

  const handleAddUnit = () =>
    onFormChange({
      ...formState,
      handlingUnits: [
        ...formState.handlingUnits,
        createEmptyHandlingUnit(formState.handlingUnits, displayWeightUnit, displayDimensionUnit),
      ],
    });

  const handleRemoveUnit = (index: number) =>
    onFormChange({ ...formState, handlingUnits: formState.handlingUnits.filter((_, idx) => idx !== index) });

  return (
    <Stack data-testid='handling-units-step' gap='md'>
      {formState.handlingUnits.map((unit, idx) => (
        <WizardHandlingUnitCard
          displayDimensionUnit={displayDimensionUnit}
          displayWeightUnit={displayWeightUnit}
          fieldErrors={fieldErrors ? sliceFieldErrors(fieldErrors, `handlingUnits.${idx}`) : undefined}
          index={idx}
          isHazmatEnabled={isHazmatEnabled}
          key={unit.id}
          onChange={(updated) => handleUnitChange(idx, updated)}
          onCommodityPresetSelect={onCommodityPresetSelect}
          onDensityAction={onDensityAction}
          onRemove={idx > 0 ? () => handleRemoveUnit(idx) : undefined}
          sellerFreightProviderId={formState.sellerFreightProviderID}
          unit={unit}
          zIndex={zIndex}
        />
      ))}
      <Anchor component='button' data-testid='add-handling-unit' onClick={handleAddUnit} size='sm' type='button'>
        <FormattedMessage {...handlingUnitsStepMessages.addHandlingUnit} />
      </Anchor>
    </Stack>
  );
};

export type HandlingUnitsStepSummaryProps = {
  formState: FreightBookingFormState;
  displayWeightUnit: WeightUnit;
};

export const HandlingUnitsStepSummary = ({ formState, displayWeightUnit }: HandlingUnitsStepSummaryProps) => {
  const intl = useIntl();

  // Commodity weights are stored in each commodity's own weightUnit (e.g. Ounces), so
  // convert every weight to the seller's preferred display unit before summing.
  const totalWeight = formState.handlingUnits.reduce(
    (unitSum, unit) =>
      unitSum +
      unit.commodities.reduce(
        (commoditySum, commodity) =>
          commoditySum + convertWeight(commodity.weight || 0, commodity.weightUnit ?? displayWeightUnit, displayWeightUnit),
        0,
      ),
    0,
  );
  const totalWeightRounded = Math.round(totalWeight * 100) / 100;

  return (
    <Stack data-testid='handling-units-summary' gap={2}>
      <Text c='var(--adl-text-primary)' fw={600} fz='sm'>
        <FormattedMessage {...handlingUnitsStepMessages.handlingSectionLabel} />
      </Text>
      <Text c='var(--adl-text-primary)' fw={400} fz='sm'>
        <FormattedMessage
          {...handlingUnitsStepMessages.summary}
          values={{
            count: formState.handlingUnits.length,
            weight: totalWeightRounded,
            unit: intl.formatMessage(WEIGHT_UNIT_ABBREVIATION[displayWeightUnit]),
          }}
        />
      </Text>
    </Stack>
  );
};
